import { AffordabilityService } from './affordability.service';
import { FormControl, UntypedFormGroup } from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { Injectable, inject } from '@angular/core';
import {
  EmploymentStatus, IApplicants, IPropertyAndLoan, MortgageType, PropertyType, StepName, errorMessages,
} from '../models';
import {
  buildSelectOptionsFromEnum,
  buildSelectOptionsFromRange,
  currencyInput,
  dateIsValid,
  defaultSelectOption,
  getAge,
  getEnumValue,
  isUnemployed,
  mapDateToUtc,
  multipleBenefitsInput,
  numberInput,
  selectInput,
  yesNoOptions,
} from '../helpers';
import { FormsValidators } from '../components/forms/validators';

export const stepInfo = {
  [StepName.PropertyAndLoan]: {
    stepName: StepName.PropertyAndLoan,
    labelText: 'Property Information',
    rightText: 'Let\'s get started with your property and amount you\'d like to borrow',
    anchorId: 'property-loan',
  },
  [StepName.Applicants]: {
    stepName: StepName.Applicants,
    labelText: 'Applicant Details',
    rightText:
      'We now need to know if you\'re applying on your own or with someone else and if you have any dependants',
    anchorId: 'applicants',
  },
  [StepName.IncomeAndExpenditure]: {
    stepName: StepName.IncomeAndExpenditure,
    labelText: 'Employment Details',
    anchorId: 'income-expenditure',
  },
  [StepName.Results]: {
    stepName: StepName.Results,
    labelText: 'Results',
    anchorId: '',
  },
};

@Injectable({
  providedIn: 'root',
})
export class FormFieldsResiService {
  private affordabilityService = inject(AffordabilityService);

  public fields: FormlyFieldConfig[] = [
    // Step 1
    {
      key: 'propertyAndLoan',
      wrappers: ['header'],
      props: {
        heading: stepInfo[StepName.PropertyAndLoan].labelText,
        tooltip: `An estimated property value is fine here as the home will be valued as part of the mortgage process.
                  The value of your property must be at least £70,000 or £100,000 for flats, maisonettes,
                  ex-council, ex-housing association or ex-Ministry of Defence properties.`,
        rightTitle: stepInfo[StepName.PropertyAndLoan].rightText,
        anchorId: stepInfo[StepName.PropertyAndLoan].anchorId,
        fullWidth: true,
      },
      validators: {
        ltvValidator: {
          expression: () => !FormsValidators.loanAmountValidator(this.affordabilityService.model.propertyAndLoan),
          message: () => FormsValidators.loanAmountValidator(this.affordabilityService.model.propertyAndLoan),
          errorPath: 'loanAmount',
        },
        propertyValidator: {
          expression: () => !FormsValidators.propertyValueValidator(this.affordabilityService.model.propertyAndLoan),
          message: () =>
            errorMessages[FormsValidators.propertyValueValidator(this.affordabilityService.model.propertyAndLoan)],
          errorPath: 'propertyValue',
        },
        lessThanDeposit: {
          expression: (control: FormControl, field: FormlyFieldConfig) => {
            const { loanAmount, propertyValue } = field.form.getRawValue().propertyAndLoan as IPropertyAndLoan;
            const equityLoanValue = control.value.equityLoanValue;
            if (!equityLoanValue) {
              return true;
            }

            return equityLoanValue < propertyValue - loanAmount;
          },
          message: 'Equity loan value must be less than deposit',
          errorPath: 'equityLoanValue',
        },
      },
      fieldGroup: [
        selectInput(
          'mortgageType',
          'Are you purchasing a new property, or re-mortgaging an existing property?',
          null,
          null,
          buildSelectOptionsFromEnum(MortgageType),
          null,
          undefined,
        ),
        selectInput(
          'propertyType', 'Property type', null, null, this.employmentStatusOptions(), null, undefined, null, {
            change: (field: FormlyFieldConfig) => field.form.controls['gap']?.reset(),
          },
        ),
        currencyInput(
          'propertyValue',
          'Property value',
          `An estimated property value is fine here as the home will be valued as part of the mortgage process.
            The value of your property must be at least £70,000,
            or £100,000 for ex-council, ex-housing association or ex-Ministry of Defence properties.`,
          null,
          [FormsValidators.greaterThan(0)],
          {
            'props.tooltip': (field: FormlyFieldConfig) => {
              return field.model?.propertyType === PropertyType.Flat
                ? `An estimated property value is fine here as the home will be valued as part of the mortgage process.
                The value of your property must be at least £100,000.`
                : `An estimated property value is fine here as the home will be valued as part of the mortgage process.
                The value of your property must be at least £70,000,
                or £100,000 for ex-council, ex-housing association or ex-Ministry of Defence properties.`;
            },
          },
        ),
        currencyInput(
          'loanAmount', 'How much would you like to borrow?', null, null, null, null, null, null, null, null, true, {
            validation: {
              messages: {
                greaterThan: errorMessages.minLoanSize,
              },
            },
          },
        ),
        selectInput(
          'gap',
          'Is your property ex-Local Authority, ex-Housing Association, or ex-Ministry of Defence?',
          null,
          (field: FormlyFieldConfig) => field.model?.propertyType !== PropertyType.House,
          [defaultSelectOption(), ...yesNoOptions()],
          null,
          undefined,
          null,
        ),
      ],
    },
    {
      type: 'template',
      props: {
        fullWidth: true,
        template: '<hr class="mt-5">',
      },
    },
    // Step 2
    {
      key: 'applicants',
      wrappers: ['header'],
      props: {
        heading: stepInfo[StepName.Applicants].labelText,
        rightTitle: stepInfo[StepName.Applicants].rightText,
        anchorId: stepInfo[StepName.Applicants].anchorId,
        fullWidth: true,
      },
      validators: {
        totalDependants: {
          expression: (control) => {
            const {
              financialDependants,
              dependantsAged0To5,
              dependantsAged6To11,
              dependantsAged12To17,
              dependantsAged18AndOver,
            } = control.value as IApplicants;
            if (!financialDependants) {
              return true;
            }
            const total = +dependantsAged0To5 + +dependantsAged6To11 + +dependantsAged12To17 + +dependantsAged18AndOver;
            return total > 0;
          },
          message: 'Total dependants must be greater than 0.',
          errorPath: 'dependantsAged18AndOver',
        },
      },
      fieldGroup: [
        {
          key: 'numberOfApplicants',
          type: 'select',
          defaultValue: 1,
          props: {
            text: 'Number of applicants',
            options: [defaultSelectOption(), { value: 1, label: '1' }, { value: 2, label: '2' }],
            formCheck: 'custom-inline',
            required: true,
            change: (field: FormlyFieldConfig) => {
              const numberOfApplicants = +field.formControl?.value;
              this.onNumberOfApplicantsChange(numberOfApplicants);
              this.fields = [...this.fields];
            },
          },
        },
        selectInput(
          'financialDependants',
          'Do you have any financial dependants?',
          `Any adult, 18 and older who lives or will live in the property and is financially dependent on the
            applicant(s) and shouldn’t include the second applicant. Also this will include any child who is 17
            or younger and lives or will live in the property and is financially dependent on the applicants.`,
          null,
          [defaultSelectOption(), ...yesNoOptions()],
          null,
          undefined,
          null,
        ),
        numberInput(
          'dependantsAged0To5',
          'Dependants aged 0-5',
          null,
          (field: FormlyFieldConfig) => !field.model.financialDependants,
        ),
        numberInput(
          'dependantsAged6To11',
          'Dependants aged 6-11',
          null,
          (field: FormlyFieldConfig) => !field.model.financialDependants,
        ),
        numberInput(
          'dependantsAged12To17',
          'Dependants aged 12-17',
          null,
          (field: FormlyFieldConfig) => !field.model.financialDependants,
        ),
        numberInput(
          'dependantsAged18AndOver',
          'Dependants aged 18 and over',
          null,
          (field: FormlyFieldConfig) => !field.model.financialDependants,
        ),
      ],
    },
    {
      type: 'template',
      props: {
        fullWidth: true,
        template: '<div class="page-break"><hr class="mt-5">',
      },
    },
    // Step 3
    {
      key: 'incomeAndExpenditure',
      wrappers: ['header'],
      props: {
        heading: stepInfo[StepName.IncomeAndExpenditure].labelText,
        anchorId: stepInfo[StepName.IncomeAndExpenditure].anchorId,
        fullWidth: true,
        suppressErrors: true,
      },
      fieldGroup: [
        {
          type: 'template',
          props: {
            fullWidth: true,
            template: '<hr class="mt-2">',
          },
        },
        {
          key: 'applicants',
          type: 'repeat',
          props: {
            text: 'Applicants',
            suppressErrors: true,
          },
          fieldArray: {
            wrappers: ['collapse'],
            props: {
              text: 'Applicant',
              subText: 'We now need to know your age and employment status',
              largerCol: true,
            },
            expressions: {
              'props.index': 'field.key',
            },
            fieldGroup: [
              {
                key: 'dateOfBirth',
                type: 'dateinput',
                props: {
                  text: 'Date of birth',
                  change: (field: FormlyFieldConfig) => {
                    const applicants = this.fields
                      .find((field) => field.key === 'incomeAndExpenditure')
                      .fieldGroup.find((field) => field.key === 'applicants');
                    const applicant = applicants.fieldGroup[applicants.fieldGroup.indexOf(field.parent)];
                    const applicantAge = getAge(mapDateToUtc(field.formControl.value));

                    if (applicantAge) {
                      applicant.fieldGroup
                        .find((field) => field.key === 'primaryIncome')
                        .fieldGroup.find((field) => field.key === 'plannedRetirementAge').props.options =
                        buildSelectOptionsFromRange(
                          applicantAge,
                          90,
                        );
                    }
                  },
                },
                validators: {
                  validation: [
                    FormsValidators.date,
                    FormsValidators.minAge(55, 'ageRange'),
                    FormsValidators.maxAge(90, 'ageRange'),
                  ],
                },
              },
              this.primaryIncomeFields('primaryIncome', true),
              // Other Income - Applicant Section
              this.templateHeaderWithSub('Other Income', 'Please enter any additional income'),
              currencyInput(
                'btlInvestmentIncome',
                'Annual Buy To Let income (averaged over the last two years)',
                `Profits from Buy To Let properties should be averaged over the last two years,
                  using the figure declared on your SA302.`,
                null,
                [FormsValidators.lessThan(100_000_001)],
              ),
              numberInput(
                'btlInvestmentIncomeBenefit',
                '% of income that would pass to remaining applicant on death',
                null,
                (field: FormlyFieldConfig) =>
                  this.affordabilityService.form.controls['applicants'].value.numberOfApplicants <= 1 ||
                  field.model?.btlInvestmentIncome <= 0,
                [FormsValidators.percentage],
                null,
                null,
                null,
                null,
                '%',
              ),
              currencyInput(
                'nonGuaranteedInvestmentIncome',
                `Annual income payments generated from an income producing investment
                (averaged over the last two years)`,
                null,
                null,
                [FormsValidators.lessThan(100_000_001)],
              ),
              numberInput(
                'nonGuaranteedInvestmentIncomeDependent',
                '% of income that would pass to remaining applicant on death',
                null,
                (field: FormlyFieldConfig) =>
                  this.affordabilityService.form.controls['applicants'].value.numberOfApplicants <= 1 ||
                  field.model?.nonGuaranteedInvestmentIncome <= 0,
                [FormsValidators.percentage],
                null,
                null,
                null,
                null,
                '%',
              ),
              currencyInput(
                'growthInvestmentAnnualIncome',
                'Growth investment annual income',
                'Growth investment annual income, calculated as 3.5% of the current fund value.',
                null,
                [FormsValidators.lessThan(100_000_001)],
              ),
              numberInput(
                'growthInvestmentAnnualIncomeDependents',
                '% of income that would pass to remaining applicant on death',
                null,
                (field: FormlyFieldConfig) =>
                  this.affordabilityService.form.controls['applicants'].value.numberOfApplicants <= 1 ||
                  field.model?.growthInvestmentAnnualIncome <= 0,
                [FormsValidators.percentage],
                null,
                null,
                null,
                null,
                '%',
              ),
              currencyInput('disabilityBenefit', 'Disability benefit (with an indefinite payment period)', null, null, [
                FormsValidators.lessThan(100_000_001),
              ]),
              currencyInput(
                'annualAdditionalIncome',
                'Annual additional income',
                `This is any additional income such as income from second jobs and
                  spousal maintenance payments (as agreed by the courts).`,
                null,
                [FormsValidators.lessThan(100_000_001)],
              ),
              {
                type: 'template',
                props: {
                  fullWidth: true,
                  template: '<div class="page-break">',
                },
              },
              // Other Income - Applicant Section
              this.templateHeaderWithSub(
                'Current retirement income', 'Please enter any retirement income you are currently receiving',
              ),
              currencyInput(
                'annualStatePension',
                'Annual state pension',
                `The full annual state pension you're
                  currently receiving.`,
                null,
                [FormsValidators.lessThan(100_000_001)],
              ),
              {
                key: 'definedContribution',
                type: 'workincomerepeat',
                props: {
                  text: `Current drawdown pension fund value after deduction of the full 25% tax free lump sum
                    (e.g. from SIPP/Defined Contribution/Personal Pension)`,
                  tooltip: `If only a partial tax-free lump sum has been taken, the fund value entered must be
                    assuming the full TFLS has been taken.`,
                  suppressErrors: true,
                },
                fieldArray: {
                  fieldGroup: [
                    multipleBenefitsInput(
                      'value',
                      null,
                      null,
                      null,
                      [FormsValidators.lessThan(100_000_001)],
                      {
                        'props.label': () => {
                          return 'Amount';
                        },
                      },
                      null,
                      null,
                      {
                        hideRequiredMarker: true,
                        labelClass: 'field-primary-label',
                        fullWidth: true,
                      },
                      'definedContribution',
                      '£',
                      null,
                    ),
                    multipleBenefitsInput(
                      'percentage',
                      null,
                      null,
                      (field: FormlyFieldConfig) =>
                        this.affordabilityService.form.controls['applicants'].value.numberOfApplicants <= 1 ||
                        field.model?.value <= 0,
                      [FormsValidators.percentage],
                      {
                        'props.label': () => {
                          return `Dependant's death
                            benefit %`;
                        },
                      },
                      null,
                      null,
                      {
                        hideRequiredMarker: true,
                        labelClass: 'field-primary-label',
                        fullWidth: true,
                      },
                      'definedContribution',
                      null,
                      '%',
                    ),
                  ],
                },
              },
              {
                key: 'annualNonEscalatingAnnuity',
                type: 'workincomerepeat',
                props: {
                  text: 'Annual non-escalating annuity',
                  tooltip: 'If income currently being received enter annual amount.',
                  suppressErrors: true,
                },
                fieldArray: {
                  fieldGroup: [
                    multipleBenefitsInput(
                      'value',
                      null,
                      null,
                      null,
                      [FormsValidators.lessThan(100_000_001)],
                      {
                        'props.label': () => {
                          return 'Amount';
                        },
                      },
                      null,
                      null,
                      {
                        hideRequiredMarker: true,
                        labelClass: 'field-primary-label',
                        fullWidth: true,
                      },
                      'annualNonEscalatingAnnuity',
                      '£',
                      null,
                    ),
                    multipleBenefitsInput(
                      'percentage',
                      null,
                      null,
                      (field: FormlyFieldConfig) =>
                        this.affordabilityService.form.controls['applicants'].value.numberOfApplicants <= 1 ||
                        field.model?.value <= 0,
                      [FormsValidators.percentage],
                      {
                        'props.label': () => {
                          return `Dependant's death
                            benefit %`;
                        },
                      },
                      null,
                      null,
                      {
                        hideRequiredMarker: true,
                        labelClass: 'field-primary-label',
                        fullWidth: true,
                      },
                      'annualNonEscalatingAnnuity',
                      null,
                      '%',
                    ),
                  ],
                },
              },
              {
                key: 'annualEscalatingAnnuity',
                type: 'workincomerepeat',
                props: {
                  text: 'Annual escalating annuity',
                  tooltip: 'If income currently being received enter annual amount.',
                  suppressErrors: true,
                },
                fieldArray: {
                  fieldGroup: [
                    multipleBenefitsInput(
                      'value',
                      null,
                      null,
                      null,
                      [FormsValidators.lessThan(100_000_001)],
                      {
                        'props.label': () => {
                          return 'Amount';
                        },
                      },
                      null,
                      null,
                      {
                        hideRequiredMarker: true,
                        labelClass: 'field-primary-label',
                        fullWidth: true,
                      },
                      'annualEscalatingAnnuity',
                      '£',
                      null,
                    ),
                    multipleBenefitsInput(
                      'percentage',
                      null,
                      null,
                      (field: FormlyFieldConfig) =>
                        this.affordabilityService.form.controls['applicants'].value.numberOfApplicants <= 1 ||
                        field.model?.value <= 0,
                      [FormsValidators.percentage],
                      {
                        'props.label': () => {
                          return `Dependant's death
                            benefit %`;
                        },
                      },
                      null,
                      null,
                      {
                        hideRequiredMarker: true,
                        labelClass: 'field-primary-label',
                        fullWidth: true,
                      },
                      'annualEscalatingAnnuity',
                      null,
                      '%',
                    ),
                  ],
                },
              },
              {
                key: 'definedBenefitFinalSalaryPension',
                type: 'workincomerepeat',
                props: {
                  text: 'Annual Defined Benefit/Final Salary Pension',
                  tooltip:
                    'The pension income you receive from a final salary, also known as a Defined Benefit Pension.',
                  suppressErrors: true,
                },
                fieldArray: {
                  fieldGroup: [
                    multipleBenefitsInput(
                      'value',
                      null,
                      null,
                      null,
                      [FormsValidators.lessThan(100_000_001)],
                      {
                        'props.label': () => {
                          return 'Amount';
                        },
                      },
                      null,
                      null,
                      {
                        hideRequiredMarker: true,
                        labelClass: 'field-primary-label',
                        fullWidth: true,
                      },
                      'definedBenefitFinalSalaryPension',
                      '£',
                      null,
                    ),
                    multipleBenefitsInput(
                      'percentage',
                      null,
                      null,
                      (field: FormlyFieldConfig) =>
                        this.affordabilityService.form.controls['applicants'].value.numberOfApplicants <= 1 ||
                        field.model?.value <= 0,
                      [FormsValidators.percentage],
                      {
                        'props.label': () => {
                          return `Dependant's death
                            benefit %`;
                        },
                      },
                      null,
                      null,
                      {
                        hideRequiredMarker: true,
                        labelClass: 'field-primary-label',
                        fullWidth: true,
                      },
                      'definedBenefitFinalSalaryPension',
                      null,
                      '%',
                    ),
                  ],
                },
              },
              {
                type: 'template',
                props: {
                  fullWidth: true,
                  template: '<div class="page-break">',
                },
              },
              // Future retirement income - Applicant Section
              this.templateHeaderWithSub(
                'Future retirement income', 'Please enter any retirement income you are not yet receiving',
              ),
              currencyInput(
                'expectedAnnualStatePension',
                'Expected annual State Pension',
                `Total annual State Pension you expect to receive when you retire. You can get your State Pension
                  forecast from GOV.UK (<a href="https://www.gov.uk/check-state-pension" target="_blank">
                  https://www.gov.uk/check-state-pension</a>).`,
                (field: FormlyFieldConfig) => field.model?.annualStatePension > 0,
                [FormsValidators.lessThan(100_000_001)],
              ),
              {
                key: 'forecastDefinedBenefitFinalSalaryPension',
                type: 'workincomerepeat',
                props: {
                  text: 'Forecast Defined Benefit/Final Salary pension',
                  tooltip: `A final salary, also known as a Defined Benefit Pension, is a type of workplace
                    pension that will pay you an income in retirement based on your salary and number
                    of years you've worked for the employer.<br> If you have one of these pensions your
                    final salary forecast can be found on your latest benefit statement.`,
                  suppressErrors: true,
                },
                fieldArray: {
                  fieldGroup: [
                    multipleBenefitsInput(
                      'value',
                      null,
                      null,
                      null,
                      [FormsValidators.lessThan(100_000_001)],
                      {
                        'props.label': () => {
                          return 'Amount';
                        },
                      },
                      null,
                      null,
                      {
                        hideRequiredMarker: true,
                        labelClass: 'field-primary-label',
                        fullWidth: true,
                      },
                      'forecastDefinedBenefitFinalSalaryPension',
                      '£',
                      null,
                    ),
                    multipleBenefitsInput(
                      'percentage',
                      null,
                      null,
                      (field: FormlyFieldConfig) =>
                        this.affordabilityService.form.controls['applicants'].value.numberOfApplicants <= 1 ||
                        field.model?.value <= 0,
                      [FormsValidators.percentage],
                      {
                        'props.label': () => {
                          return `Dependant's death
                            benefit %`;
                        },
                      },
                      null,
                      null,
                      {
                        hideRequiredMarker: true,
                        labelClass: 'field-primary-label',
                        fullWidth: true,
                      },
                      'forecastDefinedBenefitFinalSalaryPension',
                      null,
                      '%',
                    ),
                  ],
                },
              },
              {
                key: 'currentDrawdownPensionFundValue',
                type: 'workincomerepeat',
                props: {
                  text: 'Current pension fund value (SIPP/Defined Contribution/Personal Pension)',
                  tooltip: `Note, we will use 3.5% of this current pension fund value after tax free cash
                    as the assumed annual income.`,
                  suppressErrors: true,
                },
                fieldArray: {
                  fieldGroup: [
                    multipleBenefitsInput(
                      'value',
                      null,
                      null,
                      null,
                      [FormsValidators.lessThan(100_000_001)],
                      {
                        'props.label': () => {
                          return 'Amount';
                        },
                      },
                      null,
                      null,
                      {
                        hideRequiredMarker: true,
                        labelClass: 'field-primary-label',
                        fullWidth: true,
                      },
                      'currentDrawdownPensionFundValue',
                      '£',
                      null,
                    ),
                    multipleBenefitsInput(
                      'percentage',
                      null,
                      null,
                      (field: FormlyFieldConfig) =>
                        this.affordabilityService.form.controls['applicants'].value.numberOfApplicants <= 1 ||
                        field.model?.value <= 0,
                      [FormsValidators.percentage],
                      {
                        'props.label': () => {
                          return `Dependant's death
                            benefit % (as specified on the nominated beneficiary form)`;
                        },
                      },
                      null,
                      null,
                      {
                        hideRequiredMarker: true,
                        labelClass: 'field-primary-label',
                        fullWidth: true,
                      },
                      'currentDrawdownPensionFundValue',
                      null,
                      '%',
                    ),
                  ],
                },
              },
              {
                type: 'template',
                props: {
                  fullWidth: true,
                  template: '<div class="page-break">',
                },
              },
              // Future retirement income - Applicant Section
              this.templateHeader('Individual Commitments'),
              currencyInput(
                'creditCardBalance', 'Total credit card balances', null, null, [FormsValidators.lessThan(30_001)],
                {
                  'props.tooltip': (field: FormlyFieldConfig) => {
                    return `The total remaining balance of all credit cards that will remain in place on completion
                    of the mortgage for applicant ${+field.parent.key + 1}. Include any mail order agreements.
                    Note the maximum allowable unsecured debt consolidation is £30,000.`;
                  },
                },
              ),
              currencyInput(
                'regularMonthlyCommitments',
                'Regular monthly commitments',
                null,
                null,
                [FormsValidators.lessThan(100_000_001)],
                {
                  'props.tooltip': (field: FormlyFieldConfig) => {
                    return `Monthly credit commitments and committed expenditure in applicant ${+field.parent.key + 1}
                    sole name only, such as school fees or university fees. Please exclude any basic living expenditure,
                    such as transport, utilities, food, etc.`;
                  },
                },
              ),
              currencyInput(
                'councilTax',
                'Annual council tax payment',
                `If not known please leave blank, or check your council tax band at GOV.UK
                  (<a href="https://www.gov.uk/council-tax-bands" target="_blank">
                  https://www.gov.uk/council-tax-bands</a>)`,
                () => this.affordabilityService.form.controls['applicants'].value.numberOfApplicants > 1,
                [FormsValidators.lessThan(100_000_001)],
              ),
            ],
          },
        },
      ],
    },
    // Joint Commitments
    {
      key: 'applicants',
      expressions: {
        hide: (field) => field.model?.numberOfApplicants <= 1,
      },
      props: {
        fullWidth: true,
      },
      fieldGroup: [
        {
          type: 'template',
          props: {
            fullWidth: true,
            template: '<hr class="mt-5">',
          },
        },
        {
          key: 'jointCommitments',
          props: {
            fullWidth: true,
          },
          fieldGroup: [
            this.templateHeader('Joint Commitments'),
            currencyInput(
              'creditCardBalance',
              'Total joint credit card balances',
              `The total joint remaining balance of all credit cards that will remain in place on completion
                of the mortgage. Note the maximum allowable unsecured debt consolidation is £30,000.`,
              null,
              [FormsValidators.lessThan(30_001)],
            ),
            currencyInput(
              'regularMonthlyCommitments',
              'Regular monthly joint commitments',
              `Joint monthly credit commitments and committed expenditure in both applicant's names, such as school
              fees or university fees. Please exclude any basic living expenditure, such as transport, utilities,
              food, etc.`,
              null,
              [FormsValidators.lessThan(100_000_001)],
            ),
            currencyInput(
              'councilTax',
              'Annual council tax payment',
              `If not known please leave blank, or check your council tax band at GOV.UK
                (<a href="https://www.gov.uk/council-tax-bands" target="_blank">
                https://www.gov.uk/council-tax-bands</a>)`,
              null,
              [FormsValidators.lessThan(100_000_001)],
            ),
          ],
        },
      ],
    },
  ];

  public templateHeader(title: string, hideExpr: (field: FormlyFieldConfig) => boolean = null): FormlyFieldConfig {
    return {
      type: 'template',
      props: {
        fullWidth: true,
        template: `
          <h2 class="head col mt-5">${title}</h2>
        `,
      },
      expressions: {
        hide: hideExpr,
      },
    };
  }

  public templateHeaderWithSub(
    title: string,
    subTitle: string,
    hideExpr: (field: FormlyFieldConfig) => boolean = null,
  ): FormlyFieldConfig {
    return {
      type: 'template',
      props: {
        fullWidth: true,
        template: `
          <div class='pb-3 pl-3 mt-5 header'>
            <div class='d-flex'>
              <h2 class="head">${title}</h2>
            </div>
            <div class="d-flex info-text my-auto">${subTitle}</h2>
          </div>
        `,
      },
      expressions: {
        hide: hideExpr,
      },
    };
  }

  public infoMessage(title: string, hideExpr: (field: FormlyFieldConfig) => boolean = null): FormlyFieldConfig {
    return {
      type: 'template',
      props: {
        fullWidth: true,
        template: `
          <div class='d- ml-3 mb-4'>
            <div class="info-text">${title}</h2>
          </div>
        `,
      },
      expressions: {
        hide: hideExpr,
      },
    };
  }

  private onNumberOfApplicantsChange(numberOfApplicants: number) {
    if (this.affordabilityService.model.applicants.applicantAges.length > numberOfApplicants) {
      this.affordabilityService.model.applicants.applicantAges =
        this.affordabilityService.model.applicants.applicantAges.slice(
          0,
          numberOfApplicants,
        );
      this.affordabilityService.model.incomeAndExpenditure.applicants =
        this.affordabilityService.model.incomeAndExpenditure.applicants.slice(0, numberOfApplicants);
    }

    while (this.affordabilityService.model.applicants.applicantAges.length < numberOfApplicants) {
      this.affordabilityService.addApplicant();
      this.affordabilityService.addApplicantIncome();
    }
    this.affordabilityService.updateModel();
  }

  public employmentStatusOptions() {
    return [
      defaultSelectOption(),
      { value: PropertyType.House, label: 'House' },
      { value: PropertyType.Flat, label: 'Flat/Maisonette' },
    ];
  }

  private primaryIncomeFields(key: string, isPrimaryIncome: boolean): FormlyFieldConfig {
    const employmentStatusOptions = [
      defaultSelectOption(),
      { value: EmploymentStatus.Employed, label: 'Employed/Fixed term contract' },
      { value: EmploymentStatus.SelfEmployed, label: 'Self employed (sole trader/partner)' },
      { value: EmploymentStatus.DirectorOrShareholder, label: 'Director/Shareholder with 20% or more share' },
    ];

    if (isPrimaryIncome) {
      employmentStatusOptions.push(
        { value: EmploymentStatus.Homemaker, label: 'Homemaker' },
        { value: EmploymentStatus.Retired, label: 'Retired' },
        { value: EmploymentStatus.Student, label: 'Student' },
        { value: EmploymentStatus.Unemployed, label: 'Unemployed' },
      );
    }

    return {
      key,
      props: {
        fullWidth: true,
      },
      expressions: {
        hide: () => !isPrimaryIncome,
      },
      fieldGroup: [
        {
          key: 'employmentStatus',
          type: 'select',
          defaultValue: undefined,
          props: {
            text: 'Employment status',
            options: employmentStatusOptions,
            change: (field: FormlyFieldConfig, event) => {
              const employmentStatus = getEnumValue(event.target.value);
              if (isPrimaryIncome) {
                (field.form.parent as UntypedFormGroup).patchValue({
                  additionalIncome: isUnemployed(employmentStatus),
                });
              }
            },
            required: true,
          },
        },
        selectInput(
          'plannedRetirementAge',
          'Expected retirement age',
          null,
          (field: FormlyFieldConfig) => {
            return (
              !dateIsValid(field.parent.parent.model.dateOfBirth) ||
              field.parent.model?.employmentStatus === EmploymentStatus.Unemployed ||
              field.parent.model?.employmentStatus === EmploymentStatus.Retired ||
              field.parent.model?.employmentStatus === EmploymentStatus.Student ||
              field.parent.model?.employmentStatus === EmploymentStatus.Homemaker
            );
          },
          buildSelectOptionsFromRange(55, 90),
          null,
          undefined,
        ),
        this.infoMessage(
          `Please only select this option if you're fully retired,
            as only retirement income will be considered.`,
          (field: FormlyFieldConfig) => {
            return field.parent.model?.employmentStatus !== EmploymentStatus.Retired;
          },
        ),
        currencyInput(
          'grossAnnualIncome',
          'Annual gross total income',
          `Please enter your full annual basic income, car allowance and other allowances before any
            tax or deductions have been made and should exclude any allowances that are taken before tax,
            such as pension contributions. This can be evidenced using payslips, P60s or bank statements.<br><br>
            If you're in permanent employment and you're expecting a pay increase in the next three months you
            can use this amount, as long as you have a letter from your employer with confirmation
            of the increase.<br><br> If you're a Director with less than 20% share include your basic
            income here and any dividends under annual gross bonus.`,
          (field: FormlyFieldConfig) => field.parent.model?.employmentStatus !== EmploymentStatus.Employed,
          [FormsValidators.lessThan(100_000_001)],
        ),
        currencyInput(
          'annualBonusCommissionOvertime',
          'Annual Bonus/Commission/Overtime',
          `Please enter your gross annual bonus amount, overtime and commission before tax and other deductions.
            Include dividends if you're a Director with less than 20% share.
            This can be evidenced using payslips, P60s or bank statements.`,
          (field: FormlyFieldConfig) => field.parent.model?.employmentStatus !== EmploymentStatus.Employed,
          [FormsValidators.lessThan(100_000_001)],
        ),
        currencyInput(
          'netProfitForLatestPeriod',
          'Business earnings for last full year',
          `Last year's net profit (before any income tax, but post business tax), as evidenced on your SA302.<br><br>
            You must have been trading for a minimum of two years if applying with self-employed income only.<br><br>
            If you haven’t been trading during lockdown there may be additional checks carried out, this could
            impact the amount of money you can borrow.`,
          (field: FormlyFieldConfig) => field.parent.model?.employmentStatus !== EmploymentStatus.SelfEmployed,
          [FormsValidators.lessThan(100_000_001)],
        ),
        currencyInput(
          'netProfitForPreviousPeriod',
          'Business earnings for previous year',
          `Previous year's net profit (before any income tax, but post business tax).<br><br>
           You must have been trading for a minimum of two years if applying with self-employed income only.`,
          (field: FormlyFieldConfig) => field.parent.model?.employmentStatus !== EmploymentStatus.SelfEmployed,
          [FormsValidators.lessThan(100_000_001)],
        ),
        currencyInput(
          'grossRemunerationForLatestPeriod',
          'Dividends plus salary for most recent accounting period',
          `This should include the following:<br>
            - Basic salary before tax<br>
            - Plus dividends (either last year's dividends or the average of the last two years,
              whichever is lowest)<br>
            - Plus your pension contribution (up to a max of 10%) if you wish to use this against your affordability.`,
          (field: FormlyFieldConfig) => field.parent.model?.employmentStatus !== EmploymentStatus.DirectorOrShareholder,
          [FormsValidators.lessThan(100_000_001)],
        ),
        currencyInput(
          'grossRemunerationForPreviousPeriod',
          'Dividends plus salary for previous accounting period',
          `This should include the following:<br>
          - Basic salary before tax<br>
          - Plus dividends (either the last years dividends or the average of the last two years,
            whichever is lowest)<br>
          - Plus your pension contribution (up to a max of 10%).`,
          (field: FormlyFieldConfig) => field.parent.model?.employmentStatus !== EmploymentStatus.DirectorOrShareholder,
          [FormsValidators.lessThan(100_000_001)],
        ),
      ],
    };
  }
}
